import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

// 布局组件
import Layout from '@/layout/index.vue'

// 公共页面
const Login = () => import('@/views/login/index.vue')

// 仪表盘
const Dashboard = () => import('@/views/dashboard/index.vue')

// 部门管理
const DepartmentList = () => import('@/views/departments/index.vue')

// 用户管理
const UserList = () => import('@/views/users/index.vue')

// 招聘需求管理
const RecruitmentRequestList = () => import('@/views/recruitment-requests/index.vue')

// 操作记录
const OperationLogs = () => import('@/views/operation-logs/index.vue')

// 面试者管理
const CandidateList = () => import('@/views/candidates/index.vue')

// 人才库
const PersonList = () => import('@/views/persons/index.vue')

// 面试记录
const InterviewList = () => import('@/views/interviews/index.vue')

// 详情页
const DepartmentDetail = () => import('@/views/departments/detail.vue')
const UserProfile = () => import('@/views/profile/index.vue')
const RecruitmentRequestDetail = () => import('@/views/recruitment-requests/detail.vue')
const CandidateDetail = () => import('@/views/candidates/detail.vue')
const InterviewDetail = () => import('@/views/interviews/detail.vue')
const SystemSettings = () => import('@/views/system/settings.vue')

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录', requiresAuth: false }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: '仪表盘', icon: 'data-analysis', group: '工作台', groupIcon: 'home-filled', requiresAuth: true }
      },
      {
        path: 'recruitment-requests',
        name: 'RecruitmentRequestList',
        component: RecruitmentRequestList,
        meta: { title: '招聘需求', icon: 'document', group: '招聘管理', groupIcon: 'user', requiresAuth: true, roles: ['admin', 'hr', 'manager'] }
      },
      {
        path: 'recruitment-requests/:id',
        name: 'RecruitmentRequestDetail',
        component: RecruitmentRequestDetail,
        meta: { title: '需求详情', requiresAuth: true, roles: ['admin', 'hr', 'manager'], hidden: true }
      },
      {
        path: 'candidates',
        name: 'CandidateList',
        component: CandidateList,
        meta: { title: '面试者管理', icon: 'user-filled', group: '招聘管理', groupIcon: 'user', requiresAuth: true, roles: ['admin', 'hr'] }
      },
      {
        path: 'persons',
        name: 'PersonList',
        component: PersonList,
        meta: { title: '人才库', icon: 'avatar', group: '招聘管理', groupIcon: 'user', requiresAuth: true, roles: ['admin', 'hr', 'interviewer', 'manager'] }
      },
      {
        path: 'candidates/:id',
        name: 'CandidateDetail',
        component: CandidateDetail,
        meta: { title: '面试者详情', requiresAuth: true, roles: ['admin', 'hr', 'manager'], hidden: true }
      },
      {
        path: 'interviews',
        name: 'InterviewList',
        component: InterviewList,
        meta: { title: '面试记录', icon: 'chat-dot-round', group: '招聘管理', groupIcon: 'user', requiresAuth: true, roles: ['admin', 'hr', 'manager', 'interviewer'] }
      },
      {
        path: 'interviews/:id',
        name: 'InterviewDetail',
        component: InterviewDetail,
        meta: { title: '面试详情', requiresAuth: true, roles: ['admin', 'hr', 'manager', 'interviewer'], hidden: true }
      },
      {
        path: 'departments',
        name: 'DepartmentList',
        component: DepartmentList,
        meta: { title: '部门管理', icon: 'office-building', group: '组织管理', groupIcon: 'office-building', requiresAuth: true, roles: ['admin', 'hr'] }
      },
      {
        path: 'departments/:id',
        name: 'DepartmentDetail',
        component: DepartmentDetail,
        meta: { title: '部门详情', requiresAuth: true, roles: ['admin', 'hr'], hidden: true }
      },
      {
        path: 'users',
        name: 'UserList',
        component: UserList,
        meta: { title: '用户管理', icon: 'user', group: '组织管理', groupIcon: 'office-building', requiresAuth: true, roles: ['admin'] }
      },
      {
        path: 'system/operation-logs',
        name: 'OperationLogs',
        component: OperationLogs,
        meta: { title: '操作日志', icon: 'notebook', group: '系统管理', groupIcon: 'setting', requiresAuth: true, roles: ['admin'] }
      },
      {
        path: 'system/settings',
        name: 'SystemSettings',
        component: SystemSettings,
        meta: { title: '系统设置', icon: 'setting', group: '系统管理', groupIcon: 'setting', requiresAuth: true, roles: ['admin'] }
      },
      {
        path: 'profile',
        name: 'UserProfile',
        component: UserProfile,
        meta: { title: '个人资料', requiresAuth: true, hidden: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - 招聘管理系统`
  }

  // 检查是否需要认证
  if (to.meta.requiresAuth) {
    const isAuthenticated = store.getters['user/isAuthenticated']

    if (!isAuthenticated) {
      // 未登录，重定向到登录页
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // 页面刷新后 Vuex 状态丢失，用 token 重新拉取用户信息
    if (!store.getters['user/userInfo']) {
      try {
        await store.dispatch('user/getUserInfo')
      } catch (error) {
        console.error('恢复登录状态失败:', error)
        await store.dispatch('user/logout')
        next({
          path: '/login',
          query: { redirect: to.fullPath }
        })
        return
      }
    }

    // 检查角色权限
    if (to.meta.roles) {
      const userRole = store.getters['user/role']
      if (!to.meta.roles.includes(userRole) && userRole !== 'admin') {
        // 权限不足，重定向到仪表盘
        next('/dashboard')
        return
      }
    }
  }

  next()
})

// 路由错误处理
router.onError((error) => {
  console.error('路由错误:', error)
})

export default router
